import { SupplierLocationListingVO } from "./model/valueObject/location/SupplierLocationListingVO";

/**
 * Represents the lifecycle stage of a supplier location.
 *
 * This type is the single source of truth for location status coloring.
 * Both the {@link LocationApprovalWorkflow} stepper and the location list
 * tabs consume {@link LOCATION_STATUS_STYLES} via this type, so adding a
 * new status requires only one change here — not in individual components.
 *
 * Stages:
 * - `draft`       – Created and saved; required fields not yet complete.
 * - `pending`     – Submitted for admin review; not yet visible to buyers.
 * - `active`      – Approved and live; discoverable in buyer search.
 * - `deactivated` – Taken offline; contact support to reactivate.
 */
export type LocationStatus = "draft" | "pending" | "active" | "deactivated";

export interface SupplierLocationDisplayVO extends SupplierLocationListingVO {
  derivedStatus: LocationStatus;
}

/**
  * Represents the display state of a location in the buyer search results.
  *
  * This is used in reporting for filters and display of the location
  * status. It is derived from the {@link LocationStatus} and whether or
  * not the location is live
  */
export const LocationDisplayStatus = Object.freeze({
  DRAFT: "Draft",
  PENDING: "Pending",
  ACTIVE: "Active",
  DEACTIVATED: "Deactivated",
});

/**
 * Raw `locationStatus` string values returned by the API.
 *
 * - `"APPROVED"` covers both active and deactivated locations; the `live`
 *   flag distinguishes them.
 * - There is no `"DEACTIVATED"` status — deactivation is a derived state
 *   of an `"APPROVED"` location with `live === false`.
 */
export const LOCATION_API_STATUS = {
  DRAFT:     "DRAFT",
  IN_REVIEW: "IN_REVIEW",
  APPROVED:  "APPROVED",
} as const;

/**
 * Minimal shape needed to classify a location into a {@link LocationStatus}.
 * Declared locally (rather than importing `ILocationData`) to keep this
 * `types/` module free of `components/` dependencies. `ILocationData`
 * structurally satisfies this shape.
 *
 * Note: the API returns the raw lifecycle value as the field `status`
 * (e.g. `"APPROVED"`, `"DRAFT"`, `"IN_REVIEW"`), not `locationStatus`.
 */
export interface LocationStatusFields {
  /** Raw API lifecycle value — `"APPROVED"`, `"DRAFT"`, or `"IN_REVIEW"`. */
  status: string;
  live: boolean;
}

/**
 * Predicate that classifies a location into each {@link LocationStatus}.
 *
 * - `draft`       – `status === "DRAFT"`
 * - `pending`     – `status === "IN_REVIEW"`
 * - `active`      – `status === "APPROVED"` and `live === true`
 * - `deactivated` – `status === "APPROVED"` and `live === false`
 */
export const LOCATION_STATUS_MATCHER: Record<
  LocationStatus,
  (loc: LocationStatusFields) => boolean
> = {
  draft:       (loc) => loc.status === LOCATION_API_STATUS.DRAFT,
  pending:     (loc) => loc.status === LOCATION_API_STATUS.IN_REVIEW,
  active:      (loc) => loc.status === LOCATION_API_STATUS.APPROVED && loc.live,
  deactivated: (loc) => loc.status === LOCATION_API_STATUS.APPROVED && !loc.live,
};

/**
 * Derives the UI-level {@link LocationStatus} from the raw API fields of a location.
 *
 * | `status`       | `live`  | Result          |
 * |----------------|---------|-----------------|
 * | `"DRAFT"`      | any     | `"draft"`       |
 * | `"IN_REVIEW"`  | any     | `"pending"`     |
 * | `"APPROVED"`   | `true`  | `"active"`      |
 * | `"APPROVED"`   | `false` | `"deactivated"` |
 * | anything else  | any     | `"draft"`       |
 *
 * Call this at the data-fetch layer to populate `ILocationData.status` with the
 * derived {@link LocationStatus}. Pass the **raw API response object** (before
 * the derived `status` field overwrites the raw one).
 *
 * @param loc - Raw API location object with `status: string` and `live: boolean`.
 * @returns The matching {@link LocationStatus}, or `"draft"` as a safe fallback.
 */
export function deriveLocationStatus(loc: LocationStatusFields): LocationStatus {
  if (loc.status === LOCATION_API_STATUS.DRAFT)     return "draft";
  if (loc.status === LOCATION_API_STATUS.IN_REVIEW) return "pending";
  if (loc.status === LOCATION_API_STATUS.APPROVED)  return loc.live ? "active" : "deactivated";
  return "draft";
}

/**
 * Color tokens for each {@link LocationStatus}.
 *
 * Each entry provides:
 * - `bg`     – Light background for status bubbles and badge fills.
 * - `border` – Accent border/outline color.
 * - `color`  – Foreground icon/text color.
 */
export const LOCATION_STATUS_STYLES: Record<
  LocationStatus,
  { bg: string; border: string; color: string }
> = {
  draft:       { bg: "#f2f4ff", border: "#c8d0f8", color: "#2a3faa" },
  pending:     { bg: "#fffcf0", border: "#f0d87a", color: "#7a5400" },
  active:      { bg: "#e8faf0", border: "#a8dfbe", color: "#1a6636" },
  deactivated: { bg: "#fdf2f2", border: "#f5b8b8", color: "#8a1010" },
};
